import React, { FC } from "react";
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  DndContextProps
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { ExpandableSortableItem } from "@org/ui";
import {
  ContentType,
  ContentItem,
  Block,
} from "@org/ui";
import { ExerciseType } from "@org/model";
import { Button } from "@org/ui";
import { Label } from "@org/ui";
import { Input } from "@org/ui";
import { TextArea } from "@org/ui";
import { Alert, AlertTitle, AlertDescription } from "@org/ui";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@org/ui";
import { FileTextIcon, ImageIcon, VideoIcon, SquareStackIcon, InfoIcon, UndoIcon, AudioLinesIcon, MicIcon, TypeIcon, ListChecksIcon, MoreVertical, MessageCircle, ArrowRightLeft, Shuffle, UploadIcon } from "lucide-react";

type BlockContentEditorProps = {
  blocks: Block[];
  sensors: DndContextProps["sensors"];
  onDragEnd: (event: DragEndEvent, blockId?: string) => void;
  onUpdateBlock: (block: Block) => void;
  onDeleteBlock: (blockId: string) => void;
  onUndoDeleteBlock: (blockId: string) => void;
  onAddContent: (blockId: string, contentType: ContentType, exerciseSubtype?: ExerciseType) => void;
  onUpdateContent: (blockId: string, content: ContentItem) => void;
  onDeleteContent: (blockId: string, contentId: string) => void;
  onUndoDeleteContent: (blockId: string, contentId: string) => void;
  renderContent: (content: ContentItem, updateContent: (content: ContentItem) => void) => React.ReactNode;
  onMoveContent: (fromBlockId: string, toBlockId: string, contentId: string, insertIndex?: number) => void;
  onSplitBlock: (blockId: string, splitAfterContentId: string) => void;
  canDelete?: boolean;
};

export const BlockContentEditor: FC<BlockContentEditorProps> = ({
  blocks,
  sensors,
  onDragEnd,
  onUpdateBlock,
  onDeleteBlock,
  onUndoDeleteBlock,
  onAddContent,
  onUpdateContent,
  onDeleteContent,
  onUndoDeleteContent,
  renderContent,
  onMoveContent,
  onSplitBlock,
  canDelete = true,
}) => {
  return (
    <div className="mb-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => onDragEnd(event)}>
        <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {blocks.map(block => (
              block.deleted ? (
                canDelete ? (
                  <Alert key={block.id} className="my-2 flex justify-between items-center flex-row">
                    <div className="flex items-center flex-1 space-x-4">
                      <InfoIcon className="h-4 w-4" />
                      <AlertTitle>Block Deleted</AlertTitle>
                      <AlertDescription>This block has been deleted.</AlertDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => onUndoDeleteBlock(block.id)}>
                      <UndoIcon className="h-4 w-4" />
                    </Button>
                  </Alert>
                ) : null
              ) : (
                <ExpandableSortableItem
                  key={block.id}
                  item={block}
                  onUpdate={(updatedBlock) => onUpdateBlock(updatedBlock)}
                  {...(canDelete ? { onDelete: () => onDeleteBlock(block.id) } : {})}
                  defaultExpanded={block.id === blocks[blocks.length - 1]?.id}
                renderContent={(item, updateItem) => (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor={`block-name-${block.id}`}>Block Name</Label>
                        <Input
                          id={`block-name-${block.id}`}
                          value={block.name}
                          onChange={(e) => updateItem({ ...block, name: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`block-description-${block.id}`}>Description</Label>
                        <TextArea
                          id={`block-description-${block.id}`}
                          value={block.description}
                          onChange={(e) => updateItem({ ...block, description: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <BlockContentList
                      block={block}
                      allBlocks={blocks}
                      sensors={sensors}
                      onDragEnd={onDragEnd}
                      onAddContent={onAddContent}
                      onUpdateContent={onUpdateContent}
                      onDeleteContent={onDeleteContent}
                      onUndoDeleteContent={onUndoDeleteContent}
                      renderContent={renderContent}
                      onMoveContent={onMoveContent}
                      onSplitBlock={onSplitBlock}
                      canDelete={canDelete}
                    />
                  </div>
                )}
              />)
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

type BlockContentListProps = {
  block: Block;
  allBlocks: Block[];
  sensors: DndContextProps["sensors"];
  onDragEnd: (event: DragEndEvent, blockId?: string) => void;
  onAddContent: (blockId: string, contentType: ContentType, exerciseSubtype?: ExerciseType) => void;
  onUpdateContent: (blockId: string, content: ContentItem) => void;
  onDeleteContent: (blockId: string, contentId: string) => void;
  onUndoDeleteContent: (blockId: string, contentId: string) => void;
  renderContent: (content: ContentItem, updateContent: (content: ContentItem) => void) => React.ReactNode;
  onMoveContent: (fromBlockId: string, toBlockId: string, contentId: string, insertIndex?: number) => void;
  onSplitBlock: (blockId: string, splitAfterContentId: string) => void;
  canDelete?: boolean;
};

const BlockContentList: FC<BlockContentListProps> = ({
  block,
  allBlocks,
  sensors,
  onDragEnd,
  onAddContent,
  onUpdateContent,
  onDeleteContent,
  onUndoDeleteContent,
  renderContent,
  onMoveContent,
  onSplitBlock,
  canDelete = true,
}) => {
  const availableTargetBlocks = allBlocks.filter(candidate => candidate.id !== block.id && !candidate.deleted);

  const renderContentMenu = (content: ContentItem) => {
    const contentIndex = block.contents.findIndex(item => item.id === content.id);
    const hasTrailingVisibleContent = block.contents
      .slice(contentIndex + 1)
      .some(item => !item.deleted);

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="p-1 h-8 w-8">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Content actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={!availableTargetBlocks.length}>
              Move to block
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-56 overflow-y-auto">
              {availableTargetBlocks.length ? (
                availableTargetBlocks.map(targetBlock => (
                  <DropdownMenuItem
                    key={targetBlock.id}
                    onClick={() => onMoveContent(block.id, targetBlock.id, content.id)}
                  >
                    {targetBlock.name}
                  </DropdownMenuItem>
                ))
              ) : (
                <DropdownMenuItem disabled>No other blocks</DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem
            disabled={!hasTrailingVisibleContent}
            onClick={() => onSplitBlock(block.id, content.id)}
          >
            Split block here
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="mt-4">
      <div className="flex justify-between items-center mb-3">
        {/* <h3 className="text-md font-medium"></h3> */}
        <div className="flex gap-2 overflow-x-auto">
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Text)}>
            <div className="flex flex-col items-center">
              <FileTextIcon className="h-6 w-6" />
              <span className="mt-1">Text</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Image)}>
            <div className="flex flex-col items-center">
              <ImageIcon className="h-6 w-6" />
              <span className="mt-1">Image</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Video)}>
            <div className="flex flex-col items-center">
              <VideoIcon className="h-6 w-6" />
              <span className="mt-1">Video</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Audio)}>
            <div className="flex flex-col items-center">
              <AudioLinesIcon className="h-6 w-6" />
              <span className="mt-1">Audio</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.MultipleChoice)}>
            <div className="flex flex-col items-center">
              <SquareStackIcon className="h-6 w-6" />
              <span className="mt-1">M. Choice</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.LongAnswer)}>
            <div className="flex flex-col items-center">
              <FileTextIcon className="h-6 w-6" />
              <span className="mt-1">Long A.</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.ShortAnswer)}>
            <div className="flex flex-col items-center">
              <FileTextIcon className="h-6 w-6" />
              <span className="mt-1">Short A.</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.Recorded)}>
            <div className="flex flex-col items-center">
              <MicIcon className="h-6 w-6" />
              <span className="mt-1">Recorded</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.FillInBlank)}>
            <div className="flex flex-col items-center">
              <TypeIcon className="h-6 w-6" />
              <span className="mt-1">Fill Blank</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.SelectForBlank)}>
            <div className="flex flex-col items-center">
              <ListChecksIcon className="h-6 w-6" />
              <span className="mt-1">Select Blank</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.Conversational)}>
            <div className="flex flex-col items-center">
              <MessageCircle className="h-6 w-6" />
              <span className="mt-1">Convo</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.Matching)}>
            <div className="flex flex-col items-center">
              <ArrowRightLeft className="h-6 w-6" />
              <span className="mt-1">Matching</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.Scramble)}>
            <div className="flex flex-col items-center">
              <Shuffle className="h-6 w-6" />
              <span className="mt-1">Scramble</span>
            </div>
          </Button>
          <Button variant="outline" size="xl" onClick={() => onAddContent(block.id, ContentType.Exercise, ExerciseType.FileUpload)}>
            <div className="flex flex-col items-center">
              <UploadIcon className="h-6 w-6" />
              <span className="mt-1">Upload</span>
            </div>
          </Button>
        </div>
      </div>

      {block.contents.length === 0 ? (
        <Alert className="my-2">
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>No Contents</AlertTitle>
          <AlertDescription>Add content items using the buttons above.</AlertDescription>
        </Alert>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => onDragEnd(event, block.id)}>
          <SortableContext items={block.contents.map(c => c.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {block.contents.map(content => (
                content.deleted ? (
                  canDelete ? (
                    <Alert key={content.id} className="my-2 flex justify-between items-center flex-row">
                      <div className="flex items-center flex-1 space-x-4">
                        <InfoIcon className="h-4 w-4" />
                        <AlertTitle>Content Deleted</AlertTitle>
                        <AlertDescription>This content has been deleted.</AlertDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => onUndoDeleteContent(block.id, content.id)}>
                        <UndoIcon className="h-4 w-4" />
                      </Button>
                    </Alert>
                  ) : null
                ) : (
                <ExpandableSortableItem
                  key={content.id}
                  item={content}
                  onUpdate={(updatedContent) => onUpdateContent(block.id, updatedContent as ContentItem)}
                  {...(canDelete ? { onDelete: () => onDeleteContent(block.id, content.id) } : {})}
                  defaultExpanded={content.id === block.contents[block.contents.length - 1]?.id}
                  renderActions={() => renderContentMenu(content)}
                  renderContent={(contentItem, updateContentItem) =>
                    renderContent(contentItem as ContentItem, updateContentItem as (content: ContentItem) => void)
                  }
                  deleted={content.deleted}
                  />)
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};
